import { useMemo } from 'react';

import {
   ColumnType,
   StatisticsData,
   StatisticsDataHeader,
   StatisticsDataRow,
} from '@dreryk/egabinet-statistics-frontend-api';
import { Money } from '@dreryk/money-common-api';

import {
   assertUnreachable,
   Bean,
   createTranslatedText,
   dateFormatter,
   Formatter,
   integerFormatter,
   priceFormatter,
   stringFormatter,
} from '@dreryk/react-common';
import {
   ColumnWidth,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   JustifyContent,
   rowIndexKeyExtractor,
   StaticDataConfiguration,
   Table,
   TableColumnConfiguration,
   TextPlaceholder,
} from '@dreryk/react-components';

import { statisticsTranslationKeys } from '../../translations';

const NO_VALUE = '-';
const EMPTY_VALUE = '';

type StatisticsPreviewTableProps = {
   readonly data: StatisticsData;
};

type StatisticsDataType = Record<string, string | Money>;

const getFormatter = (type: ColumnType): Formatter<string> | Formatter<Date> | Formatter<number> => {
   switch (type) {
      case 'DATE':
         return dateFormatter;
      case 'NUMBER':
         return integerFormatter;
      case 'TEXT':
         return stringFormatter;
      case 'MONEY':
         return priceFormatter;
      default:
         return assertUnreachable(type);
   }
};

const getJustifyContent = (type: ColumnType): JustifyContent => {
   switch (type) {
      case 'NUMBER':
      case 'MONEY':
         return JustifyContent.END;
      default:
         return JustifyContent.START;
   }
};

const getHorizontalAlign = (type: ColumnType): HorizontalAlign => {
   switch (type) {
      case 'NUMBER':
      case 'MONEY':
         return HorizontalAlign.RIGHT;
      default:
         return HorizontalAlign.LEFT;
   }
};

const customValueConverter = (bean: Bean, header: StatisticsDataHeader): unknown => {
   const value = bean[header.name];
   if (value === NO_VALUE || value === EMPTY_VALUE) {
      return null;
   }

   if (value instanceof Money) {
      return value.asNumber();
   }
   return value;
};

const mapDynamicColumnsWidth = (headers: StatisticsDataHeader[]): Record<string, ColumnWidth> =>
   Object.assign(
      {},
      ...headers.map((header) => ({
         [header.name]: header.width,
      }))
   );

const useStatisticsPreviewColumn = (
   headers: StatisticsDataHeader[]
): TableColumnConfiguration<StatisticsDataType>[] => {
   const columnsWidth = useMemo(() => createDynamicColumnsWidth(mapDynamicColumnsWidth(headers)), [headers]);

   return useMemo<TableColumnConfiguration<StatisticsDataType>[]>(
      () =>
         headers.map((header) => ({
            name: header.name,
            accessor: (bean) => customValueConverter(bean, header),
            placeholder: TextPlaceholder.NO_DATA,
            label: createTranslatedText(header.name),
            width: columnsWidth[header.name],
            sortingDisabled: true,
            formatter: getFormatter(header.type),
            justifyContent: getJustifyContent(header.type),
            textAlign: getHorizontalAlign(header.type),
         })),
      [headers, columnsWidth]
   );
};

const applyCellsDataToSingleRow = (headerNames: string[], row: StatisticsDataRow): StatisticsDataType =>
   Object.assign(
      {},
      ...headerNames.map((headerName, index) => ({
         [headerName]: row.cells[index],
      }))
   );

const useStatisticsPreviewDataConfiguration = (
   headers: StatisticsDataHeader[],
   rows: StatisticsDataRow[]
): StaticDataConfiguration<StatisticsDataType> => {
   return useMemo(() => {
      const headerNames = headers.map((header) => header.name);
      const rowsData = rows.map((row) => applyCellsDataToSingleRow(headerNames, row));
      return { data: rowsData };
   }, [rows, headers]);
};

export const StatisticsPreviewTableEmptyStatePlaceholder = (): JSX.Element => (
   <EmptyStatePlaceholder
      heading={statisticsTranslationKeys.preview.noResultsLabel}
      subheading={statisticsTranslationKeys.preview.noResultsSubText}
      testId={'noResults'}
   />
);

export const StatisticsPreviewTable = (props: StatisticsPreviewTableProps): JSX.Element => {
   const { headers, rows } = props.data;

   const columns = useStatisticsPreviewColumn(headers);
   const dataConfiguration = useStatisticsPreviewDataConfiguration(headers, rows);

   return (
      <Table<StatisticsDataType>
         testId={'StatisticsPreviewTable'}
         columns={columns}
         keyExtractor={rowIndexKeyExtractor}
         dataConfiguration={dataConfiguration}
         compact
         placeholder={<StatisticsPreviewTableEmptyStatePlaceholder />}
         pageSize={Infinity}
      />
   );
};
